<template>
  <form @submit.prevent="submitForm" class="uf-form">
    <!-- ==================== Bio Data ==================== -->
    <section class="uf-section">
      <div class="section-head">
        <div class="section-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21a8 8 0 0116 0" stroke-linecap="round" />
          </svg>
        </div>
        <div>
          <div class="section-title">Bio Data</div>
          <div class="section-sub">Basic identity of the bank account holder.</div>
        </div>
      </div>

      <div class="grid-2">
        <div class="field" :class="{ 'field-invalid': !name.isValid }">
          <label for="name">Name</label>
          <div class="input-wrap">
            <input
              type="text"
              id="name"
              v-model.trim="name.val"
              placeholder="Account holder name"
              @focus="clearValidity('name')"
            />
          </div>
          <p v-if="!name.isValid" class="field-error">Name must not be empty.</p>
        </div>

        <div class="field" :class="{ 'field-invalid': !username.isValid }">
          <label for="username">Username</label>
          <div v-if="mode === 'update'" class="input-readonly">
            <span>{{ username.val }}</span>
            <span class="readonly-badge">Locked</span>
          </div>
          <div v-else class="input-wrap">
            <input
              type="text"
              id="username"
              v-model.trim="username.val"
              placeholder="Login username"
              @focus="clearValidity('username')"
            />
          </div>
          <p v-if="!username.isValid" class="field-error">Username must not be empty.</p>
        </div>

        <div class="field" :class="{ 'field-invalid': !email.isValid }">
          <label for="email">Email <span class="field-optional">(optional)</span></label>
          <div class="input-wrap">
            <input
              type="email"
              id="email"
              v-model.trim="email.val"
              placeholder="you@example.com"
              @focus="clearValidity('email')"
            />
          </div>
          <p v-if="!email.isValid" class="field-error">{{ email.msg }}</p>
        </div>

        <div class="field" :class="{ 'field-invalid': !phone.isValid }">
          <label for="phone">Phone <span class="field-optional">(optional)</span></label>
          <div class="input-wrap">
            <input
              type="text"
              id="phone"
              v-model.trim="phone.val"
              maxlength="10"
              placeholder="10-digit number"
              @focus="clearValidity('phone')"
              @keypress="isNumber($event)"
            />
          </div>
          <p v-if="!phone.isValid" class="field-error">{{ phone.msg }}</p>
        </div>

        <div class="field field-full" :class="{ 'field-invalid': !password.isValid }">
          <label for="password">
            Password
            <span v-if="mode === 'update'" class="field-optional">(leave empty to keep current)</span>
          </label>
          <div class="input-wrap">
            <input
              :type="showPassword ? 'text' : 'password'"
              id="password"
              v-model.trim="password.val"
              :placeholder="mode === 'create' ? 'Set a password' : 'New password (optional)'"
              autocomplete="new-password"
              @focus="clearValidity('password')"
            />
            <button
              type="button"
              class="eye-btn"
              @click="showPassword = !showPassword"
              tabindex="-1"
              :title="showPassword ? 'Hide' : 'Show'"
            >
              <svg v-if="!showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke-linecap="round" stroke-linejoin="round" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.77 19.77 0 015.06-6.06M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.5 19.5 0 01-3.17 4.19M14.12 14.12A3 3 0 019.88 9.88" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M1 1l22 22" stroke-linecap="round" />
              </svg>
            </button>
          </div>
          <p v-if="!password.isValid" class="field-error">Password must not be empty.</p>
        </div>
      </div>
    </section>

    <!-- ==================== Payment Data ==================== -->
    <section class="uf-section">
      <div class="section-head">
        <div class="section-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="3" y="6" width="18" height="12" rx="2" />
            <path d="M3 10h18M7 15h4" stroke-linecap="round" />
          </svg>
        </div>
        <div>
          <div class="section-title">Payment Data</div>
          <div class="section-sub">UPI-only. Upload a QR to auto-detect the ID.</div>
        </div>
      </div>

      <div class="grid-2">
        <div class="field">
          <label>Payment Method</label>
          <div class="input-readonly">
            <span class="method-tag">UPI</span>
            <span class="readonly-hint">Banks only support UPI.</span>
          </div>
        </div>

        <div class="field" :class="{ 'field-invalid': !upiId.isValid }">
          <label for="upi-id">UPI ID</label>
          <div v-if="mode === 'update' && upiId.val" class="input-readonly">
            <span class="upi-value">{{ upiId.val }}</span>
            <span class="readonly-badge">Locked</span>
          </div>
          <div v-else class="input-wrap">
            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <rect x="3" y="6" width="18" height="12" rx="2" />
              <path d="M7 15h4" stroke-linecap="round" />
            </svg>
            <input
              type="text"
              id="upi-id"
              v-model.trim="upiId.val"
              placeholder="e.g. user@bank or upload QR"
              @focus="clearValidity('upiId')"
            />
          </div>
          <p v-if="!upiId.isValid" class="field-error">UPI Id is required.</p>
        </div>
      </div>

      <!-- QR Upload (create mode only) -->
      <div v-if="mode === 'create'" class="qr-block">
        <div class="qr-block-label">QR Code <span class="field-optional">(auto-detects UPI ID)</span></div>
        <div
          class="qr-drop"
          :class="{ 'has-file': qrPreview, 'is-invalid': !qrPicture.isValid }"
          @click="onPickFile('qrPicture')"
        >
          <input ref="qrPicture" class="hidden-input" id="qr-picture" @change="handleUploadedQR" type="file" accept="image/*" />
          <div v-if="!qrPreview" class="qr-empty">
            <div class="qr-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <path d="M14 14h3M20 14v3M14 20h3M20 20h1" stroke-linecap="round" />
              </svg>
            </div>
            <div class="qr-copy">
              <div class="qr-title">Click to upload QR code</div>
              <div class="qr-sub">JPG, JPEG, PNG · max 2MB</div>
            </div>
          </div>
          <div v-else class="qr-preview-wrap">
            <img class="qr-preview" :src="qrPreview" alt="QR preview" />
            <button type="button" class="qr-clear" @click.stop="cancelUploadedPhoto" title="Remove QR">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </div>
        <p v-if="!qrPicture.isValid" class="field-error">{{ qrPicture.msg }}</p>
      </div>
    </section>

    <!-- ==================== Restrictions & Limits ==================== -->
    <section class="uf-section">
      <div class="section-head">
        <div class="section-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" stroke-linecap="round" />
          </svg>
        </div>
        <div>
          <div class="section-title">Restrictions & Limits</div>
          <div class="section-sub">
            <span v-if="canEditOrderAmount">Vendor-controlled. Applies to today's traffic — resets at 12:00 AM IST.</span>
            <span v-else>Vendor-managed. Contact your vendor to change these values.</span>
          </div>
        </div>
      </div>

      <div class="grid-2">
        <div class="field" :class="{ 'field-invalid': !minOrderAmount.isValid }">
          <label for="min-order-amount">Min Order Amount</label>
          <div v-if="canEditOrderAmount" class="input-wrap">
            <span class="input-prefix">₹</span>
            <input
              type="number"
              id="min-order-amount"
              v-model.trim="minOrderAmount.val"
              min="0"
              step="100"
              placeholder="e.g. 300"
              @focus="clearValidity('minOrderAmount')"
            />
          </div>
          <div v-else class="input-readonly" title="Only the vendor can change this value.">
            <span>{{ minOrderAmount.val != null && minOrderAmount.val !== '' ? fmtNum(minOrderAmount.val) : 'Not set' }}</span>
            <span class="readonly-badge">Vendor</span>
          </div>
          <p v-if="!minOrderAmount.isValid" class="field-error">{{ minOrderAmount.msg }}</p>
        </div>

        <div class="field" :class="{ 'field-invalid': !maxOrderAmount.isValid }">
          <label for="max-order-amount">Max Order Amount</label>
          <div v-if="canEditOrderAmount" class="input-wrap">
            <span class="input-prefix">₹</span>
            <input
              type="number"
              id="max-order-amount"
              v-model.trim="maxOrderAmount.val"
              min="0"
              step="1000"
              placeholder="e.g. 100000"
              @focus="clearValidity('maxOrderAmount')"
            />
          </div>
          <div v-else class="input-readonly" title="Only the vendor can change this value.">
            <span>{{ maxOrderAmount.val != null && maxOrderAmount.val !== '' ? fmtNum(maxOrderAmount.val) : 'Not set' }}</span>
            <span class="readonly-badge">Vendor</span>
          </div>
          <p v-if="!maxOrderAmount.isValid" class="field-error">{{ maxOrderAmount.msg }}</p>
        </div>

        <div class="field" :class="{ 'field-invalid': !maxOrders.isValid }">
          <label for="max-orders">Max Orders (per day)</label>
          <div v-if="canEditOrderAmount" class="input-wrap">
            <input
              type="number"
              id="max-orders"
              v-model.trim="maxOrders.val"
              min="100"
              placeholder="e.g. 1000"
              @focus="clearValidity('maxOrders')"
            />
          </div>
          <div v-else class="input-readonly" title="Only the vendor can change this value.">
            <span>{{ maxOrders.val != null && maxOrders.val !== '' ? fmtNum(maxOrders.val) : 'Not set' }}</span>
            <span class="readonly-badge">Vendor</span>
          </div>
          <p class="field-hint">Counts today's orders only. Resets at 12:00 AM IST.</p>
          <p v-if="!maxOrders.isValid" class="field-error">{{ maxOrders.msg }}</p>
        </div>

        <div class="field" :class="{ 'field-invalid': !perDayPayinLimit.isValid }">
          <label for="per-day-payin-limit">Daily Pay-In Limit</label>
          <div v-if="canEditDailyPayinLimit" class="input-wrap">
            <span class="input-prefix">₹</span>
            <input
              type="number"
              id="per-day-payin-limit"
              v-model.trim="perDayPayinLimit.val"
              min="0"
              step="1000"
              placeholder="e.g. 500000"
              @focus="clearValidity('perDayPayinLimit')"
            />
          </div>
          <div v-else class="input-readonly" title="Only the vendor can change this value.">
            <span>{{ perDayPayinLimit.val != null && perDayPayinLimit.val !== '' ? fmtNum(perDayPayinLimit.val) : 'Not set' }}</span>
            <span class="readonly-badge">Vendor</span>
          </div>
          <p class="field-hint">
            Resets every day at 12:00 AM IST.
            <span v-if="mode === 'update' && usedTodayDisplay" class="hint-strong">
              Used today: <strong>₹{{ usedTodayDisplay }}</strong><span v-if="dailyLimitDisplay"> of ₹{{ dailyLimitDisplay }}</span>.
            </span>
          </p>
          <p v-if="!perDayPayinLimit.isValid" class="field-error">
            {{ perDayPayinLimit.msg || 'Daily Pay-In Limit must not be empty.' }}
          </p>
        </div>
      </div>
    </section>

    <!-- Extension Auto-Approve — HIDDEN (preserved for future re-enable). -->
    <template v-if="false">
      <section class="uf-section">
        <div class="section-head">
          <div class="section-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div>
            <div class="section-title">Extension Auto-Approve</div>
            <div class="section-sub">Chrome extension detects payments on the bank portal and auto-approves matching orders.</div>
          </div>
        </div>

        <div class="ext-row">
          <label class="switch">
            <input type="checkbox" v-model="extensionEnabled" @change="toggleExtension" />
            <span class="ext-slider round"></span>
          </label>
          <div v-if="extensionLoading" class="ext-loading">Setting up…</div>
        </div>

        <div v-if="extensionEnabled && extensionCredentials" class="ext-creds">
          <div class="ext-creds-head">Extension credentials</div>
          <div class="ext-creds-sub">Copy these values into the Chrome extension. Keep the Public Key confidential.</div>
          <div class="cred-row">
            <label>API Endpoint</label>
            <div class="cred-line">
              <input type="text" :value="extensionCredentials.apiEndpoint" readonly />
              <base-copy-button :text="extensionCredentials.apiEndpoint" title="Copy Endpoint" />
            </div>
          </div>
          <div class="cred-row">
            <label>Salt</label>
            <div class="cred-line">
              <input type="text" :value="extensionCredentials.salt" readonly class="mono" />
              <base-copy-button :text="extensionCredentials.salt" title="Copy Salt" />
            </div>
          </div>
          <div class="cred-row">
            <label>Public Key (Base64)</label>
            <div class="cred-line">
              <textarea :value="extensionCredentials.publicKeyBase64" readonly class="mono cred-textarea"></textarea>
              <base-copy-button :text="extensionCredentials.publicKeyBase64" title="Copy Public Key" />
            </div>
          </div>
        </div>
        <div v-if="extensionEnabled && !extensionCredentials && !extensionLoading" class="ext-warn">
          Extension is enabled but credentials are unavailable. Try toggling off and on again.
        </div>
      </section>
    </template>

    <!-- ==================== Footer / submit ==================== -->
    <div class="uf-foot">
      <p v-if="!formIsValid" class="foot-alert">Please fix the errors above and try again.</p>
      <button type="submit" class="btn btn-primary btn-large">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
          <path v-if="mode === 'create'" d="M12 5v14M5 12h14" stroke-linecap="round" />
          <path v-else d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {{ mode === 'create' ? 'Create bank' : 'Save changes' }}
      </button>
    </div>
  </form>
</template>

<script>
import http from "../../http-common.js";

export default {
  name: "create-user-form",
  emits: ["create-user"],
  props: {
    mode: String,
    selectedUser: Object,
  },
  mounted() {
    if (this.mode == "update") {
      this.setUser();
    } else {
      this.perDayPayinLimit.val = 500000;
      this.minOrderAmount.val = 300;
      this.maxOrderAmount.val = 100000;
      this.maxOrders.val = 1000;
    }
    // Load jsQR for QR code decoding
    if (!window.jsQR) {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js";
      script.async = true;
      document.head.appendChild(script);
    }
  },
  data() {
    return {
      name:            { val: "",   isValid: true, msg: "" },
      username:        { val: "",   isValid: true, msg: "" },
      email:           { val: "",   isValid: true, msg: "" },
      phone:           { val: "",   isValid: true, msg: "" },
      password:        { val: "",   isValid: true, msg: "" },
      upiId:           { val: "",   isValid: true, msg: "" },
      openingBalance:  { val: 0,    isValid: true, msg: "" },
      payInLimit:      { val: 0,    isValid: true, msg: "" },
      // Vendor-set daily payin allowance template. Drives the
      // "Daily Pay In Limit" form field. Writes to users.per_day_payin_limit
      // server-side; backend applies the X − Y math to also rewrite
      // payInLimit (today's working remainder) atomically.
      perDayPayinLimit:{ val: null, isValid: true, msg: "" },
      payOutLimit:     { val: 0,    isValid: true, msg: "" },
      payOut:          { val: false, isValid: true, msg: "" },
      paymentMethod:   { val: "UPI", isValid: true, msg: "" },
      minOrderAmount:  { val: null, isValid: true, msg: "" },
      maxOrderAmount:  { val: null, isValid: true, msg: "" },
      maxOrders:       { val: null, isValid: true, msg: "" },
      maxBalance:      { val: null, isValid: true, msg: "" },
      qrPicture:       { val: null, isValid: true, msg: "" },
      qrPreview: null,
      showPassword: false,
      formIsValid: true,
      // Extension auto-approve
      extensionEnabled: false,
      extensionCredentials: null,
      extensionLoading: false,
    };
  },
  computed: {
    // Min/Max order amount cap a bank's eligibility range for new payin
    // routing. Only vendor-level operators (admin / superadmin and the
    // forward-compat 'vendor' role) may change them — branches and banks
    // see a read-only display.
    canEditOrderAmount() {
      const role = this.$store.getters.role;
      return ["vendor", "admin", "superadmin"].includes(role);
    },
    canEditDailyPayinLimit() {
      const role = this.$store.getters.role;
      return ["vendor", "admin", "superadmin"].includes(role);
    },
    // "Used today" hint = previous template − current working.
    usedTodayDisplay() {
      const u = this.selectedUser;
      if (!u) return "";
      const template = parseFloat(u.per_day_payin_limit);
      const working = parseFloat(u.payInLimit);
      if (!isFinite(template)) return "";
      const used = Math.max(0, template - (isFinite(working) ? working : 0));
      return used.toLocaleString("en-IN");
    },
    dailyLimitDisplay() {
      const u = this.selectedUser;
      if (!u) return "";
      const template = parseFloat(u.per_day_payin_limit);
      if (!isFinite(template)) return "";
      return template.toLocaleString("en-IN");
    },
  },
  methods: {
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    fmtNum(v) {
      const n = Number(v);
      if (!isFinite(n)) return String(v);
      return "₹" + n.toLocaleString("en-IN");
    },
    extractUPIFromQRData(qrString) {
      // QR code typically contains a UPI deep link like:
      // upi://pay?pa=upiid@bank&pn=Name&...
      try {
        const upiMatch = qrString.match(/[?&]pa=([^&]+)/i);
        if (upiMatch) {
          return decodeURIComponent(upiMatch[1]);
        }
        const plainMatch = qrString.match(/[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+/);
        if (plainMatch) {
          return plainMatch[0];
        }
      } catch (e) {
        console.error("Error parsing QR data:", e);
      }
      return null;
    },
    async extractUPIFromQR() {
      if (!this.qrPicture.val) return;

      const waitForJsQR = () => new Promise((resolve) => {
        if (window.jsQR) return resolve();
        const interval = setInterval(() => {
          if (window.jsQR) { clearInterval(interval); resolve(); }
        }, 100);
      });

      try {
        await waitForJsQR();
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = window.jsQR(imageData.data, imageData.width, imageData.height);
            if (code && code.data) {
              const upi = this.extractUPIFromQRData(code.data);
              if (upi) {
                this.upiId.val = upi;
                this.qrPicture.isValid = true;
                this.qrPicture.msg = "";
              } else {
                this.upiId.val = "";
                this.qrPicture.isValid = false;
                this.qrPicture.msg = "QR code does not contain a UPI ID. Please enter the UPI ID manually.";
              }
            } else {
              this.upiId.val = "";
              this.qrPicture.isValid = false;
              this.qrPicture.msg = "QR code not detected. Please make sure the image is clear and try again.";
            }
          };
          img.src = event.target.result;
        };
        reader.readAsDataURL(this.qrPicture.val);
      } catch (error) {
        console.error("Error extracting UPI from QR:", error);
      }
    },
    validateForm() {
      this.formIsValid = true;

      if (this.mode == "create") {
        if (this.username.val === "") { this.username.isValid = false; this.formIsValid = false; }
        if (this.password.val === "") { this.password.isValid = false; this.formIsValid = false; }
      }

      if (this.name.val === "")  { this.name.isValid  = false; this.formIsValid = false; }
      if (this.upiId.val === "") { this.upiId.isValid = false; this.formIsValid = false; }

      // payInLimit (today's working remainder) is no longer a form field —
      // it's derived from per_day_payin_limit + the X − Y math on the server.
      if (this.payOutLimit.val === "") {
        this.payOutLimit.isValid = false;
        this.formIsValid = false;
      }
    },
    submitForm() {
      this.validateForm();
      if (!this.formIsValid) return;

      let formData = {
        username: this.username.val,
        name: this.name.val,
        email: this.email.val,
        phone: this.phone.val,
        upiid: this.upiId.val,
        payIn: 1, // Default true and hidden
        payOut: this.payOut.val ? 1 : 0,
        payOutLimit: this.payOutLimit.val,
        balance: this.openingBalance.val,
        paymentMethod: "UPI",
        is_utr_enabled: 1,
        isPayNow: 1,
        website: null,
        minOrderAmount: this.minOrderAmount.val,
        maxOrderAmount: this.maxOrderAmount.val,
        maxOrders: this.maxOrders.val,
        maxBalance: this.maxBalance.val,
        qr_picture: this.qrPicture.val,
        qr: this.mode == "create" ? "" : this.selectedUser.qr,
        qrPreview: this.qrPreview,
      };

      // Daily payin limit — emission policy differs per mode.
      // CREATE: send whatever is on screen, for EVERY role.
      // UPDATE: only emit when the vendor actually changed the value.
      if (this.mode === "create") {
        if (this.perDayPayinLimit.val != null && this.perDayPayinLimit.val !== "") {
          formData.per_day_payin_limit = this.perDayPayinLimit.val;
        }
      } else if (this.canEditDailyPayinLimit) {
        const original =
          this.selectedUser && this.selectedUser.per_day_payin_limit != null
            ? parseFloat(this.selectedUser.per_day_payin_limit)
            : null;
        const current =
          this.perDayPayinLimit.val != null && this.perDayPayinLimit.val !== ""
            ? parseFloat(this.perDayPayinLimit.val)
            : null;
        if (current !== original) {
          formData.per_day_payin_limit = current;
        }
      }

      if (this.mode == "create") {
        formData.password = this.password.val;
      } else if (this.password.val != "") {
        formData.password = this.password.val;
      }

      this.$emit("create-user", formData);
    },
    isNumber(evt) {
      evt = evt ? evt : window.event;
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    async toggleExtension() {
      this.extensionLoading = true;
      try {
        const response = await http.post(
          `/users/${this.username.val}/extension-setup`,
          { enabled: this.extensionEnabled }
        );
        if (response.data && response.data.success) {
          if (this.extensionEnabled && response.data.credentials) {
            this.extensionCredentials = response.data.credentials;
          } else {
            this.extensionCredentials = null;
          }
        } else {
          this.extensionEnabled = !this.extensionEnabled;
          alert(response.data?.message || "Failed to update extension settings.");
        }
      } catch (error) {
        this.extensionEnabled = !this.extensionEnabled;
        alert(error.response?.data?.message || error.message || "Failed to update extension settings.");
      }
      this.extensionLoading = false;
    },
    async loadExtensionCredentials() {
      if (!this.extensionEnabled) return;
      try {
        const response = await http.get(`/users/${this.username.val}/extension-credentials`);
        if (response.data && response.data.success && response.data.credentials) {
          this.extensionCredentials = response.data.credentials;
        }
      } catch (e) {
        console.error("Failed to load extension credentials:", e);
      }
    },
    setUser() {
      this.name.val = this.selectedUser.name;
      this.username.val = this.selectedUser.username;
      this.email.val = this.selectedUser.email;
      this.phone.val = this.selectedUser.phone;
      this.upiId.val = this.selectedUser.upiId;
      this.openingBalance.val = this.selectedUser.balance;
      this.payOut.val = this.selectedUser.payOut ? true : false;
      this.payInLimit.val = this.selectedUser.payInLimit;
      this.payOutLimit.val = this.selectedUser.payOutLimit;
      this.minOrderAmount.val = this.selectedUser.minOrderAmount || null;
      this.maxOrderAmount.val = this.selectedUser.maxOrderAmount || null;
      this.maxOrders.val = this.selectedUser.maxOrders || null;
      this.maxBalance.val = this.selectedUser.maxBalance || null;
      this.perDayPayinLimit.val =
        this.selectedUser.per_day_payin_limit != null
          ? this.selectedUser.per_day_payin_limit
          : null;
      this.extensionEnabled = !!this.selectedUser.extension_enabled;
      if (this.extensionEnabled) {
        this.loadExtensionCredentials();
      }
      if (this.selectedUser.qr) {
        this.qrPreview = this.selectedUser.qr;
      }
    },
    cancelUploadedPhoto() {
      this.qrPicture.val = null;
      this.qrPreview = null;
    },
    onPickFile(ref) {
      this.$refs[ref].click();
    },
    handleUploadedQR(event) {
      this.qrPicture.isValid = true;
      this.qrPicture.msg = "";
      const file = event.target.files[0];
      if (file) {
        if (!file.type.includes("image/")) {
          this.qrPicture.isValid = false;
          this.qrPicture.msg = "Only JPG, JPEG and PNG files are allowed.";
        } else if (file.size > 2 * 1024 * 1024) {
          this.qrPicture.isValid = false;
          this.qrPicture.msg = "File size must be less than 2MB.";
        } else {
          this.qrPicture.isValid = true;
          this.qrPicture.msg = "";
          this.qrPicture.val = file;
          this.qrPreview = URL.createObjectURL(file);
          this.extractUPIFromQR();
        }
      }
    },
  },
};
</script>

<style scoped>
/* ==================== Form ==================== */
.uf-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 4px 0 8px;
}

/* ==================== Section card ==================== */
.uf-section {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 16px;
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-shadow: 0 8px 22px -16px rgba(27, 42, 71, 0.18);
}

.section-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f4f0e5;
}
.section-icon {
  width: 40px; height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #eef2f7 0%, #f4f0e5 100%);
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.section-icon svg { width: 20px; height: 20px; }
.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1b2a47;
  line-height: 1.2;
  letter-spacing: -0.005em;
}
.section-sub {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.4;
}

/* ==================== Grid ==================== */
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.field-full { grid-column: 1 / -1; }

@media (max-width: 720px) {
  .grid-2 { grid-template-columns: 1fr; }
}

/* ==================== Field ==================== */
.field {
  display: flex;
  flex-direction: column;
}
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
  letter-spacing: 0.01em;
}
.field-optional {
  font-weight: 400;
  color: #94a3b8;
  font-size: 11.5px;
  margin-left: 4px;
}

.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.input-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.input-prefix {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 13px;
  pointer-events: none;
}
.input-wrap input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.input-wrap input::placeholder { color: #cbd5e1; }
.input-wrap:has(.input-icon) input { padding-left: 38px; }
.input-wrap:has(.input-prefix) input { padding-left: 26px; }
.input-wrap:has(.eye-btn) input { padding-right: 40px; }
.input-wrap input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.field-invalid .input-wrap input {
  border-color: #fca5a5;
  background: #fef2f2;
}

.eye-btn {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 28px; height: 28px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
}
.eye-btn:hover { background: #f1f5f9; color: #475569; }
.eye-btn svg { width: 16px; height: 16px; }

/* ---------- Read-only display ---------- */
.input-readonly {
  padding: 10px 12px;
  border: 1px solid #ede9df;
  border-radius: 10px;
  background: #fbfaf7;
  color: #475569;
  font-size: 13px;
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: space-between;
}
.readonly-badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8371;
  background: #f4f0e5;
  border: 1px solid #ede9df;
  padding: 3px 8px;
  border-radius: 999px;
  flex-shrink: 0;
}
.readonly-hint {
  color: #94a3b8;
  font-size: 11.5px;
  font-style: italic;
}
.method-tag {
  font-weight: 700;
  color: #1b2a47;
  letter-spacing: 0.05em;
}
.upi-value {
  font-family: "SFMono-Regular", Menlo, monospace;
  color: #1b2a47;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-error {
  font-size: 12px;
  color: #dc2626;
  margin: 6px 0 0;
  font-weight: 500;
}
.field-hint {
  font-size: 11.5px;
  color: #94a3b8;
  margin: 6px 0 0;
  line-height: 1.4;
}
.hint-strong { color: #475569; }
.hint-strong strong { color: #1b2a47; font-weight: 700; }

/* ==================== QR Upload ==================== */
.qr-block {
  border-top: 1px solid #f4f0e5;
  padding-top: 16px;
}
.qr-block-label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
  letter-spacing: 0.01em;
}
.qr-drop {
  border: 2px dashed #cbd5e1;
  border-radius: 14px;
  padding: 22px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fbfaf7;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}
.qr-drop:hover { border-color: #94a3b8; background: #f4f0e5; }
.qr-drop.has-file { border-color: #10b981; background: #ecfdf5; padding: 14px; }
.qr-drop.is-invalid { border-color: #fca5a5; background: #fef2f2; }

.qr-empty {
  display: flex;
  align-items: center;
  gap: 14px;
}
.qr-icon {
  width: 52px; height: 52px;
  border-radius: 12px;
  background: #fff;
  color: #64748b;
  border: 1px solid #e2e8f0;
  display: flex; align-items: center; justify-content: center;
}
.qr-icon svg { width: 26px; height: 26px; }
.qr-copy { display: flex; flex-direction: column; gap: 2px; }
.qr-title { font-size: 13px; font-weight: 600; color: #1b2a47; }
.qr-sub { font-size: 11.5px; color: #94a3b8; }

.qr-preview-wrap {
  position: relative;
  display: inline-flex;
}
.qr-preview {
  max-height: 160px;
  max-width: 240px;
  border-radius: 10px;
  background: #fff;
  padding: 8px;
  border: 1px solid #a7f3d0;
}
.qr-clear {
  position: absolute;
  top: -8px;
  left: -8px;
  width: 26px; height: 26px;
  border-radius: 999px;
  background: #dc2626;
  color: #fff;
  border: 2px solid #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.qr-clear:hover { transform: scale(1.08); }
.qr-clear svg { width: 12px; height: 12px; }

.hidden-input { display: none; }

/* ==================== Footer / submit ==================== */
.uf-foot {
  display: flex;
  align-items: center;
  gap: 14px;
  justify-content: flex-end;
  flex-wrap: wrap;
  padding-top: 4px;
}
.foot-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  margin: 0;
  font-weight: 500;
  flex: 1;
  min-width: 200px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
}
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 8px 20px -8px rgba(27, 42, 71, 0.45);
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px -10px rgba(27, 42, 71, 0.55);
}
.btn-large { padding: 12px 22px; font-size: 14px; }
.btn-icon { width: 15px; height: 15px; }

/* ==================== Extension section (currently hidden) ==================== */
.ext-row { display: flex; align-items: center; gap: 12px; }
.ext-loading { font-size: 13px; color: #64748b; }
.switch { position: relative; display: inline-block; width: 50px; height: 28px; }
.switch input { opacity: 0; width: 0; height: 0; }
.ext-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background: #ccc; transition: 0.3s; }
.ext-slider:before { content: ""; position: absolute; height: 20px; width: 20px; left: 4px; bottom: 4px; background: #fff; transition: 0.3s; }
.switch input:checked + .ext-slider { background: #1B2A47; }
.switch input:checked + .ext-slider:before { transform: translateX(22px); }
.ext-slider.round { border-radius: 28px; }
.ext-slider.round:before { border-radius: 50%; }

.ext-creds {
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ext-creds-head {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 700;
  color: #8a8371;
}
.ext-creds-sub {
  font-size: 12px;
  color: #64748b;
}
.cred-row label {
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
  display: block;
}
.cred-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.cred-line input,
.cred-line textarea {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 12px;
  background: #fff;
  color: #334155;
}
.mono { font-family: "SFMono-Regular", Menlo, monospace; }
.cred-textarea { min-height: 60px; resize: vertical; }

.ext-warn {
  padding: 10px 12px;
  border: 1px solid #fde68a;
  background: #fefce8;
  color: #92400e;
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 500;
}
</style>
